import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  selectCurrentGroup,
  selectGitlabGroups,
  selectGitlabGroupsLoading,
  selectGroupDetailLoading,
  updateGroup,
  syncGroup,
  clearCurrentGroup,
  clearError,
  fetchGitlabGroups,
  type IGroupMapping,
} from '../reducers/scimManagementSlice';
import GroupMappingEditor from './GroupMappingEditor';
import usePlatformToken from '../hooks/usePlatformToken';

const ScimGroupDetail = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const group = useAppSelector(selectCurrentGroup);
  const loading = useAppSelector(selectGroupDetailLoading);
  const gitlabGroups = useAppSelector(selectGitlabGroups);
  const gitlabGroupsLoading = useAppSelector(selectGitlabGroupsLoading);
  const { token } = usePlatformToken();

  const [displayName, setDisplayName] = useState('');
  const [mappings, setMappings] = useState<IGroupMapping[]>([]);
  const [saved, setSaved] = useState(false);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (token !== null) {
      void dispatch(fetchGitlabGroups({ platformToken: token }));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (group != null) {
      setDisplayName(group.display_name);
      setMappings(group.group_mappings ?? []);
      setSaved(false);
    }
  }, [group]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current !== null) {
        clearTimeout(savedTimerRef.current);
      }
    };
  }, []);

  if (group == null) return null;

  const members = group.members ?? [];

  const handleSave = (): void => {
    dispatch(clearError());
    void dispatch(
      updateGroup({
        groupId: group.id,
        display_name: displayName,
        group_mappings: mappings,
      }),
    ).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        void dispatch(syncGroup(group.id));
        setSaved(true);
        if (savedTimerRef.current !== null) {
          clearTimeout(savedTimerRef.current);
        }
        savedTimerRef.current = setTimeout(() => {
          setSaved(false);
        }, 3000);
      }
    });
  };

  return (
    <div className="bg-blue-50 border-b border-gray-200 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-dataops-dark text-base">Edit Group</h2>
        <button
          onClick={() => dispatch(clearCurrentGroup())}
          className="text-secondary-text text-sm hover:text-primary-text"
        >
          Close
        </button>
      </div>

      <div>
        <label className="block text-sm text-secondary-text mb-1" htmlFor="group-display-name">
          Display Name
        </label>
        <input
          id="group-display-name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full"
        />
      </div>

      <div>
        <p className="text-sm text-secondary-text mb-1">External ID</p>
        <p className="text-sm text-primary-text font-mono">{group.scim_external_id}</p>
      </div>

      <div>
        <p className="text-sm text-secondary-text mb-2">Dataops Group Mappings</p>
        <GroupMappingEditor
          mappings={mappings}
          onChange={setMappings}
          gitlabGroups={gitlabGroups}
          gitlabGroupsLoading={gitlabGroupsLoading}
          platformToken={token}
        />
      </div>

      <div>
        <p className="text-sm text-secondary-text mb-1">Members ({members.length})</p>
        {members.length === 0 && <p className="text-sm text-gray-500">No members in this group</p>}
        <ul className="text-sm space-y-1">
          {members.map((m) => (
            <li key={m.id} className="text-primary-text">
              {m.email}
              {m.name != null && m.name.length > 0 && <span className="text-secondary-text ml-1">({m.name})</span>}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={loading}
          className="bg-dataops-primary-blue hover:bg-hover-primary-blue text-white rounded px-4 py-1.5 text-sm disabled:opacity-50"
        >
          {loading ? 'Saving…' : 'Save & Sync'}
        </button>
        {saved && <span className="text-green-highlight text-sm">Saved</span>}
      </div>
    </div>
  );
};

export default ScimGroupDetail;
